import React, { useMemo } from 'react'
import cx from 'classnames'

import { AdoptionComponent } from '../../../constants/AdoptionComponent'
import { useDsAdoptionAttribute } from '../../../utils/dsAdoption'
import CheckboxGroupContext from '../CheckboxGroup/CheckboxGroup-Context'
import { FormChoiceField } from '../FormField'

import { CheckboxContainer } from './CheckboxContainer'
import { CheckboxProps } from './interfaces'

import cmCheckbox from './Checkbox.module.css'
import cmCheckboxContainer from './CheckboxContainer.module.css'
/** The Checkbox component
 *
 * **Clickable area: checkbox + label**.
 * Additional texts like `lozenge`, `helpText`, and `errorText` are **not clickable** and should not have `cursor: pointer`.
 * `infoText` still uses `cursor: pointer` because it shows a tooltip on hover, but clicking it does **not** toggle the control.
 *
 *[Figma](https://www.figma.com/file/Xf38PdCKeISWnBoh0pLFIIWf/%F0%9F%A7%B1-MC-Desktop-Components?type=design&node-id=18575-48035&mode=design&t=SHla2EuBPM68Q2Eo-0)
 */
export const Checkbox = ({
  labelId,
  checked = false,
  disabled = false,
  infoText,
  errorText,
  className,
  fullWidth,
  orientation = 'right',
  invalid = false,
  indeterminate = false,
  id,
  lozenge,
  helpText,
  value,
  onChange,
  ...props
}: CheckboxProps) => {
  const {
    values: selectedGroupValues,
    handleChange: handleOnGroupValueChange,
    disabled: isGroupDisabled,
  } = React.useContext(CheckboxGroupContext)

  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.CHECKBOX)

  const isChecked = useMemo(() => {
    // if group context is present, the checkbox value is managed by checkbox group
    if (selectedGroupValues !== undefined) {
      if (value) {
        return selectedGroupValues.includes(String(value))
      }
    }
    return checked
  }, [checked, selectedGroupValues, value])

  const isDisabled = disabled || isGroupDisabled

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    value && handleOnGroupValueChange?.(String(value), event)
    onChange?.(event)
  }

  return (
    <FormChoiceField
      id={id}
      value={value}
      type="checkbox"
      checked={isChecked}
      form={props.form}
      onChange={isDisabled ? undefined : handleChange}
      invalid={invalid}
      disabled={isDisabled}
      labelId={labelId}
      lozenge={lozenge}
      infoText={infoText}
      infoTextWidth={props.infoTextWidth}
      errorText={errorText}
      helpText={helpText}
      tabIndex={props.tabIndex}
      data-indeterminate={indeterminate}
      className={className}
      fieldClasses={{
        choice: cx(cmCheckboxContainer.checkbox__input, {
          [cmCheckbox.inputOrientationLeft]: orientation === 'left',
        }),
        labelWrapper: cx({
          [cmCheckbox.checkbox__labelWrapperWide]: orientation === 'left' && fullWidth,
        }),
        containerWithLabel: cx({
          [cmCheckbox.labelRight]: orientation === 'right',
          [cmCheckbox.labelLeft]: orientation === 'left',
        }),
      }}
      fullWidth={fullWidth}
      inputRef={props.inputRef}
      {...props}
      {...adoptionAttribute}
    >
      <CheckboxContainer
        checked={isChecked}
        disabled={isDisabled}
        indeterminate={indeterminate}
        invalid={invalid}
      />
    </FormChoiceField>
  )
}
